<script setup>
import { ref, onMounted, watch } from 'vue';
import { condaApi } from '../condaApi';

const EMPTY = () => ({ slug: '', category: '', description: '', response: '', status: 'published', keywords: [], exclude_keywords: [] });

const entries = ref([]);
const cats = ref([]);
const q = ref('');
const cat = ref('');
const sel = ref(null);
const draft = ref(null);
const kwInput = ref('');
const exInput = ref('');
const saving = ref(false);
const err = ref('');
const newCat = ref(false); // category picker is in "type a new one" mode

function catChange() {
  if (draft.value.category === '__new__') { draft.value.category = ''; newCat.value = true; }
}

async function load() { entries.value = await condaApi.listKb(q.value, cat.value); }
async function loadCats() { cats.value = await condaApi.categories(); }
onMounted(() => { load(); loadCats(); });
watch([q, cat], load);

function open(e) { sel.value = e.slug; draft.value = JSON.parse(JSON.stringify(e)); err.value = ''; newCat.value = false; }
function create() { sel.value = null; draft.value = EMPTY(); err.value = ''; newCat.value = false; }

async function save() {
  saving.value = true; err.value = '';
  try {
    // New entries only: warn when the KB likely already covers this. Warn-only —
    // the operator decides; edits skip the check (they ARE the existing entry).
    if (!draft.value.id) {
      const similar = (await condaApi.kbSimilar(draft.value).catch(() => []))
        .filter(s => s.slug !== draft.value.slug);
      if (similar.length) {
        const ok = confirm(
          `The knowledge base may already cover this:\n\n${similar.map(s => `• ${s.slug}`).join('\n')}\n\nCreate “${draft.value.slug}” anyway? (Consider editing the existing entry instead.)`
        );
        if (!ok) { saving.value = false; return; }
      }
    }
    const saved = await condaApi.saveKb(draft.value); await load(); await loadCats(); open(saved);
  }
  catch (e) { err.value = e.message; }
  saving.value = false;
}
async function remove() {
  if (!draft.value?.slug || !confirm(`Delete "${draft.value.slug}"?`)) return;
  await condaApi.deleteKb(draft.value.slug); draft.value = null; sel.value = null; load();
}
function addKw(field, model) {
  const v = (field === 'keywords' ? kwInput : exInput).value.trim();
  if (!v || !draft.value) return;
  if (!draft.value[field].includes(v)) draft.value[field].push(v);
  if (field === 'keywords') kwInput.value = ''; else exInput.value = '';
}
function rmKw(field, v) { draft.value[field] = draft.value[field].filter(k => k !== v); }
</script>

<template>
  <div class="conda-page">
    <div class="head">
      <h1>Knowledge — FAQ</h1>
      <button class="btn primary" @click="create">+ New entry</button>
    </div>
    <div class="row filters">
      <input v-model="q" placeholder="Search…" class="in" style="max-width:240px" />
      <select v-model="cat" class="in" style="max-width:220px">
        <option value="">All categories</option>
        <option v-for="c in cats" :key="c.name" :value="c.name">{{ c.name }} ({{ c.count }})</option>
      </select>
      <span class="muted">{{ entries.length }} entries</span>
    </div>

    <div class="split">
      <div class="list scroll">
        <div v-for="e in entries" :key="e.slug" class="item" :class="{ sel: sel === e.slug }" @click="open(e)">
          <strong>{{ e.slug }}</strong>
          <div class="muted sm">{{ e.description || e.response.slice(0, 80) }}</div>
          <span v-if="e.category" class="tag">{{ e.category }}</span>
        </div>
        <div v-if="!entries.length" class="empty">No entries</div>
      </div>

      <div class="scroll" style="display:flex;flex-direction:column;min-height:0;overflow:hidden">
        <div v-if="!draft" class="empty" style="margin:auto">Select an entry or create a new one</div>
        <div v-else class="card" style="flex:1 1 auto;min-height:0;overflow:auto">
          <div v-if="err" class="banner err">{{ err }}</div>
          <label>
            Name / ID
            <span class="info" title="A short, unique label for this entry — letters, numbers and underscores only (e.g. operating_hours). It's used internally to find and link the entry; customers never see it.">ⓘ</span>
          </label>
          <input v-model="draft.slug" :disabled="!!sel" class="in" placeholder="operating_hours" />
          <div class="muted sm">Internal id (letters, numbers, underscores). Customers never see this.</div>

          <label>Category</label>
          <select v-if="!newCat" v-model="draft.category" class="in" @change="catChange">
            <option value="">(none)</option>
            <option v-for="c in cats" :key="c.name" :value="c.name">{{ c.name }}</option>
            <option value="__new__">➕ New category…</option>
          </select>
          <div v-else class="row" style="gap:6px">
            <input v-model="draft.category" class="in" placeholder="new_category_name" style="flex:1" />
            <button class="btn" type="button" title="Choose an existing category" @click="newCat = false; draft.category = ''">↩</button>
          </div>
          <label>Description (one-line summary)</label>
          <input v-model="draft.description" class="in" />
          <label>Response (verified content injected into the bot)</label>
          <textarea v-model="draft.response" class="in" style="min-height:160px" />

          <label>Keywords (trigger phrases)</label>
          <div>
            <span v-for="k in draft.keywords" :key="k" class="kw">{{ k }}<button @click="rmKw('keywords', k)">✕</button></span>
          </div>
          <input v-model="kwInput" class="in" placeholder="Add keyword, press Enter" @keydown.enter="addKw('keywords')" />

          <label>Exclude keywords (suppress this entry)</label>
          <div>
            <span v-for="k in draft.exclude_keywords" :key="k" class="kw">{{ k }}<button @click="rmKw('exclude_keywords', k)">✕</button></span>
          </div>
          <input v-model="exInput" class="in" placeholder="Add exclude keyword, press Enter" @keydown.enter="addKw('exclude_keywords')" />

          <div class="row between" style="margin-top:18px">
            <button class="btn primary" :disabled="saving || !draft.slug || !draft.response" @click="save">
              {{ saving ? 'Saving…' : sel ? 'Save changes' : 'Create entry' }}
            </button>
            <button v-if="sel" class="btn danger" @click="remove">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped src="./conda.css"></style>
